import { AccessReportRunStatusChangeNotificationMessagePayload, AccessReportStatusEnum, AccessReportUsageReportChangeNotificationMessagePayload } from "@app/access-reports/dashboard";
import { AccessReportMaintJobStatusEnum } from "@app/access-reports/admin";
import { EmployeeGroup } from "@app/access-reports/shared";
import { JobStatus, JobType } from "../jobs";
import { AccuracyGapAnalysisStatus, ProjectRunConfigurationStatus, ProjectRunStatusEnum } from "../projects/enumerators";
import { SummaryResultNetworkType } from "../shared/data-summaries/enumerations";
import { PciDashboardStatusEnum } from "@app/projects/network-assessment/pci-dashboard/shared"

/** The possible types of messages the notification context can receive. */
enum MessageType {
  SystemMaintenance = "systemMaintenance",
  JobStatusChange = "jobStatusChange",
  SummariesResultsAvailable = "summariesResultsAvailable",
  ReportComplete = "reportComplete",
  ProjectRunStatusChange = "projectRunStatusChange",
  ClientGapLinkResultsAvailable = "clientGapLinkResultsAvailable",
  ProcessingProjectionAnalysis = "processingProjectionAnalysis",
  ProcessingProviderChanges = "processingProviderChanges",
  ProcessingSpecialtyGroupExceptions = "processingSpecialtyGroupExceptions",
  MultiProjectRunStatusChange = "multiProjectRunStatusChange",
  AccessReportsRunStatusChange = "accessReportsRunStatusChange",
  ProcessingProspectMarketNcp = "processingProspectMarketNcp",
  AccuracyGapAnalysisRunStatusChange = "accuracyGapAnalysisRunStatusChange",
  AccessReportsEmployeeGroupUploadStatusChange = "accessReportsEmployeeGroupUploadStatusChange",
  AccessReportsUsageReportStatusChange = "accessReportsUsageReportStatusChange",
  BulkTargetProjectRunStatusChange = "bulkTargetProjectRunStatusChange",
  AccessReportsProviderSetCopyStatusChange = "accessReportsProviderSetCopyStatusChange",
  PciDashboardStatusChange = "pciDashboardStatusChange",
  ZipCodeAnalysisStatusChangeNotification = "zipCodeAnalysisStatusChange",
  ProviderValidationStatusChange = "providerValidationStatusChange"
}

/** Represents a message received via our SignalR/WebSocket pipe. */
interface Message {
  notificationType: MessageType;
  targetUserId?: number;
  payload?: JobStatusChangePayload
  | SummariesResultsAvailablePayload
  | ReportCompletePayload
  | ProjectRunStatusChangePayload
  | MultiProjectRunStatusChangePayload
  | AccessReportRunStatusChangeNotificationMessagePayload
  | AccessReportsEmployeeGroupUploadStatusChangePayload
  | AccessReportUsageReportChangeNotificationMessagePayload
  | AccessReportMaintJobStatusChangeNotificationMessagePayload
  | PciDashboardStatusChangeNotificationMessagePayload
  | ZipCodeAnalysisStatusChangeNotificationMessagePayload
  | ProviderValidationStatusChangeNotificationMessagePayload;
}

interface JobStatusChangePayload {
  jobId: number;
  userId: number;
  clientId: number;
  jobType: JobType;
  projectId?: number;
  payload?: string;
  dateCreated: Date;
  dateQueued?: Date;
  dateStarted?: Date;
  dateCancelled?: Date;
  dateFinished?: Date;
  success?: boolean;
  errorMessage?: boolean;

  jobStatus: JobStatus;
}

interface ClientGapResultsAvailableNotificationMessagePayload {
  clientId: number;
  jobStatus: JobStatus;
}

interface SummariesResultsAvailablePayload {
  projectId: number;
  projectRunConfigurationId?: number;
  networkType: SummaryResultNetworkType;
}
interface ReportCompletePayload {
  reportId: number;
  reportName: string;
}
interface ProjectRunStatusChangePayload {
  projectId: number;
  projectRunConfigurationId: number;
  status: ProjectRunConfigurationStatus;
  isPublished: boolean;
  isReadOnly: boolean;
  isEditRestricted: boolean;
}
interface MultiProjectRunStatusChangePayload {
  customProjectIds: number[];
  isPublished: boolean;
  status: ProjectRunConfigurationStatus;
  readOnly: boolean;
}

interface AccessReportsRunStatusChangePayload {
  accessReportId: number;
  jobStatus: JobStatus;
}

interface AccuracyGapAnalysisStatusChangeNotificationMessagePayload {
  projectId?: number;
  projectRunConfigurationId: number;
  companionProjectRunConfigurationId?: number;
  accuracyGapAnalysisStatus?: AccuracyGapAnalysisStatus;
}

interface AccessReportsEmployeeGroupUploadStatusChangePayload extends EmployeeGroup {
  status: AccessReportStatusEnum;
  uploadMessage: string;
}

interface BulkTargetProjectRunStatusChangeNotificationMessagePayload {
  projectId?: number;
  projectRunConfigurationId: number;
  companionProjectRunConfigurationId?: number;
  accuracyGapAnalysisStatus?: AccuracyGapAnalysisStatus;
  status: ProjectRunConfigurationStatus;
}

interface AccessReportMaintJobStatusChangeNotificationMessagePayload {
  providerSetCopyId: number;
  providerSetId: number;
  providerSetVersionId: number;
  jobStatus: AccessReportMaintJobStatusEnum
}

interface PciDashboardStatusChangeNotificationMessagePayload {
  projectId: number
  projectRunConfigurationId: number
  status: PciDashboardStatusEnum
}

interface ZipCodeAnalysisStatusChangeNotificationMessagePayload {
  projectId: number;
  projectRunConfigurationId: number;
  companionProjectRunConfigurationId?: number;
  projectRunStatus: ProjectRunConfigurationStatus & ProjectRunStatusEnum;
}

interface ProviderValidationStatusChangeNotificationMessagePayload {
  providerSetId: number;
  providerSetVersionId: number;
}

export {
  ClientGapResultsAvailableNotificationMessagePayload,
  JobStatusChangePayload,
  Message as default,
  MessageType,
  MultiProjectRunStatusChangePayload,
  ProjectRunStatusChangePayload,
  ReportCompletePayload,
  SummariesResultsAvailablePayload,
  AccessReportsRunStatusChangePayload,
  AccuracyGapAnalysisStatusChangeNotificationMessagePayload,
  AccessReportMaintJobStatusChangeNotificationMessagePayload,
  AccessReportsEmployeeGroupUploadStatusChangePayload,
  BulkTargetProjectRunStatusChangeNotificationMessagePayload,
  PciDashboardStatusChangeNotificationMessagePayload,
  ZipCodeAnalysisStatusChangeNotificationMessagePayload,
  ProviderValidationStatusChangeNotificationMessagePayload
};
